// components/BuyerDashboard/DetailedRFQ.js
import { useState, useEffect, useCallback } from "react";
import { useParam } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ApiHelper } from "../../../utils/ApiHelper";
import { REQUIREMENT_DETAIL, UPDATE_REQUIREMENT } from "../../../lib/API_KEYS";
import RaiseConcernModal from "../../AdminDashboard/RegistrationForms/RaiseConcernModal";

export default function DetailedRFQ({ id, userRole }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectionReason, setRejectionReason] = useState("");
    const [showConcernModal, setShowConcernModal] = useState(false);
    const [concern, setConcern] = useState("");


    // Fetch RFQ details
    const fetchRequirement = useCallback(async () => {
        if (!id) return;

        setLoading(true);
        try {
            const response = await new ApiHelper().FetchFromServer(
                REQUIREMENT_DETAIL[0].replace(":requirementId", id),
                REQUIREMENT_DETAIL[1],
            );

            setData(response?.data || null);
        } catch (err) {
            console.error("Error fetching RFQ details:", err);
            toast.error("Failed to fetch RFQ details.");
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        if (id) fetchRequirement();
    }, [id, fetchRequirement]);

    const getBadgeStyles = (status) => {
        switch (status) {
            case "Rejected":
                return {
                    container: {
                        background: "#FDE2E2",
                        color: "#D32F2F",
                        borderRadius: "999px",
                        padding: "4px 10px",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                        border: "1px solid #D32F2F",
                    },
                    dot: { width: 10, height: 10, borderRadius: 6, background: "#D32F2F" },
                };
            case "Seller Intimation":
                return {
                    container: {
                        background: "#E1F5E4",
                        color: "#2E7D32",
                        borderRadius: "999px",
                        padding: "4px 10px",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                        border: "1px solid #2E7D32",
                    },
                    dot: { width: 10, height: 10, borderRadius: 6, background: "#2E7D32" },
                };
            default:
                return {
                    container: {
                        background: "#D7E3F2",
                        color: "#0064B2",
                        borderRadius: "999px",
                        padding: "4px 10px",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                        border: "1px solid #0064B2",
                    },
                    dot: { width: 10, height: 10, borderRadius: 6, background: "#0064B2" },
                };
        }
    };

    const updateStatus = async (newStatus, reason = "") => {
        if (newStatus === "Rejected" && !reason.trim()) {
            toast.error("Please provide a rejection reason!");
            return;
        }

        try {
            await new ApiHelper().FetchFromServer(
                UPDATE_REQUIREMENT[0].replace(":requirementId", id),
                UPDATE_REQUIREMENT[1],
                undefined, { status: newStatus, rejectionReason: reason }
            );
            toast.success(`Status updated to "${newStatus}"!`);
            setShowRejectModal(false);
            setRejectionReason("");
            fetchRequirement();
        } catch (err) {
            console.error("Error updating status:", err);
            toast.error("Failed to update status. Please try again.");
        }
    };

    const handleOpenConcern = () => setShowConcernModal(true);
    const handleCloseConcern = () => {
        setShowConcernModal(false);
        setConcern("");
    };

    const handleSubmitConcern = async () => {
        if (!concern.trim()) return toast.error("Concern cannot be empty!");
        try {

            const res = await new ApiHelper().FetchFromServer(
                UPDATE_REQUIREMENT[0].replace(":requirementId", id),
                UPDATE_REQUIREMENT[1],
                undefined, { status: "Query Raised", concern }
            );

            if (!res.data) {
                const err = await res.data;
                throw new Error(err.message || "Failed to submit concern");
            }
            toast.success("Concern submitted successfully!")
            handleCloseConcern();
        } catch (err) {
            return toast.error("Failed to submit concern. Please try again.");
        }
    };

    if (!data) return <div className="text-center my-5">Loading...</div>;

    const { companyName, status, createdAt, title, industry, service, description } = data;
    const { container: badgeStyles, dot: dotStyles } = getBadgeStyles(status);

    const smallFields = [
        { label: "Title", value: title },
        { label: "Industry", value: industry },
        { label: "Services", value: service },
    ];

    const attributes = [
        { title: "Quantity", value: data.quantity, icon: "/images/quantity.svg" },
        { title: "Material", value: data.material, icon: "/images/material.svg" },
        { title: "Quality", value: data.quality, icon: "/images/quality.svg" },
        { title: "Package", value: data.packaging, icon: "/images/package.svg" },
        { title: "Logistics", value: data.logistics, icon: "/images/logistics.svg" },
        { title: "Delivery Timelines", value: data.delivery, icon: "/images/delivery.svg" },
        { title: "Seller Location", value: data.supplierLocation, icon: "/images/location.svg" },
    ];

    const headerStyles = { color: "#071437", fontSize: "1.4rem" };

    return (
        <div className="container my-3">
            <ToastContainer position="top-right" autoClose={3000} />

            {/* Header */}
            <div className="row align-items-center">
                <div className="col-12 col-md-6">
                    <div className="d-flex align-items-center gap-3 flex-wrap">
                        <div style={headerStyles}>{companyName}</div>
                        <div style={badgeStyles}>
                            <span style={dotStyles} />
                            <span>{status}</span>
                        </div>
                    </div>
                    <div style={{ color: "#A2A2A2", marginTop: 6 }}>
                        {new Date(createdAt).toLocaleString("en-US", {
                            month: "short",
                            day: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                        })} (
                        {Math.floor((new Date() - new Date(createdAt)) / (1000 * 60 * 60 * 24))} days ago)
                    </div>
                </div>

                {userRole == 'admin' && (<div className="col-12 col-md-6 text-md-end mt-3 mt-md-0">
                    <div className="d-inline-flex flex-wrap gap-2 justify-content-md-end">
                        <button
                            className="btn border-black d-flex align-items-center" style={{ color: "#000" }}
                            onClick={handleOpenConcern}
                        >
                            <img src="/images/query.svg" style={{ marginRight: 6 }} alt="query" />
                            <span>Raise Query</span>
                        </button>


                        <button
                            className="btn border-black d-flex align-items-center"
                            style={{ color: "#000" }}
                            onClick={() => setShowRejectModal(true)}
                        >
                            <img src="/images/reject.svg" style={{ marginRight: 6 }} alt="reject" />
                            <span>Reject</span>
                        </button>

                        {/* Accept */}
                        <button
                            className="btn d-flex align-items-center"
                            style={{ background: "#181D4C", color: "#fff" }}
                            onClick={() => updateStatus("Seller Intimation")}
                        >
                            <img src="/images/accept.svg" style={{ marginRight: 6 }} alt="accept" />
                            <span>Accept</span>
                        </button>
                    </div>
                </div>)}
            </div>

            {/* Fields */}
            <div className="row mt-4 g-3">
                {smallFields.map((f, i) => (
                    <div key={i} className="col-12 col-sm-6 col-md-4">
                        <div className="p-3 rounded border" style={{ border: "1px solid #eee", wordBreak: "break-word" }}>
                            <div className="d-flex flex-wrap align-items-center gap-2">
                                <div style={{ color: "#000", flexShrink: 0 }}>{f.label}:</div>
                                <div style={{ color: "#C0C0C0" }}>{f.value}</div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Description */}
            <div className="row mt-4">
                <div className="col-12">
                    <div className="p-3 rounded border" style={{ border: "1px solid #eee" }}>
                        <div style={{ color: "#000" }}>Brief Description</div>
                        <p style={{ color: "#A2A2A2", marginTop: 8 }}>{description}</p>
                    </div>
                </div>
            </div>

            {/* Attributes */}
            <div className="row mt-4 g-3">
                {attributes.map((a) => (
                    <div key={a.title} className="col-12 col-sm-6 col-md-3">
                        <div className="p-3 rounded border h-100" style={{ border: "1px solid #eee" }}>
                            <img src={a.icon} alt={a.title} style={{ width: 28, height: 28 }} />
                            <div style={{ marginTop: 8, color: "#000" }}>{a.title}</div>
                            <div style={{ color: "#C0C0C0", marginTop: 6 }}>{a.value}</div>
                        </div>
                    </div>
                ))}
            </div>

            <RaiseConcernModal
                show={showConcernModal}
                onClose={() => setShowConcernModal(false)}
                onSubmit={() => {
                    handleSubmitConcern(concern);
                }}
                concern={concern}
                setConcern={setConcern}
            />

            {showRejectModal && (
                <div
                    className="modal-overlay"
                    onClick={(e) => e.target.classList.contains("modal-overlay") && setShowRejectModal(false)}
                >
                    <div className="modal-content p-4 rounded" onClick={(e) => e.stopPropagation()}>
                        <h4 style={{ color: "#181D4C", marginBottom: 12, textAlign: "start" }}>Reject RFQ</h4>
                        <hr />
                        <p className="text-muted" style={{ textAlign: "start" }}>
                            Are you sure you want to reject RFQ? This action is permanent and can’t be restored.
                        </p>
                        <label htmlFor="reason" className="form-label mt-3" style={{ textAlign: "start" }}>
                            Rejection reason
                        </label>
                        <textarea
                            id="reason"
                            className="form-control"
                            style={{ height: 100, resize: "none", border: "1px solid #d2ccccff", borderRadius: 6 }}
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                        />
                        <div className="d-flex gap-2 mt-3">
                            <button
                                className="btn border-dark flex-fill"
                                style={{ background: "#fff", color: "#000", borderColor: "#000" }}
                                onClick={() => { setShowRejectModal(false); setRejectionReason(""); }}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn flex-fill"
                                style={{ background: "#181D4C", color: "#fff" }}
                                onClick={() => updateStatus("Rejected", rejectionReason)}
                            >
                                Reject RFQ
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 9999;
          padding: 10px; 
        }
        .modal-content {
          background: #fff;
          width: 100%;  
          max-width: 400px; 
          border-radius: 8px;
          padding: 20px;
        }
        .modal-content textarea,
        .modal-content input {
          border: 1px solid #d2ccccff; 
          border-radius: 6
        padding: 8px;
        resize: none;         
        width: 100%;
        box-sizing: border-box;
        }

        .modal-content textarea:focus,
        .modal-content input:focus {
        outline: none;
        box-shadow: none;
        border-color: #181D4C; 
        }
`}</style>
        </div>
    );
}
